import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import { Alert, Button, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function PositionList({ isAuthenticated, user }) {
  const positionIdPrefix = "position-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/positions/all",
    }).response;
    try {
      const pos = await response.body.json();
      const normalized = (pos || []).map((row) => ({
        ...row,
        value: row.value ?? row.position ?? "",
      }));
      return normalized;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localpositions = await getData();
        setData(localpositions || []);
      } catch (e) {
        alert(e);
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/positions/new" : `/positions/${encodeURIComponent(record.id)}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, position: record.value, last_edited_by: user } }
      });
      return await result.response;
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
  const handleSave = async ({ row, column, value }) => {
    const recordToSave = { ...row.original, [column.id]: value };

    // Validation
    if (recordToSave.new_record && !recordToSave.value) return;
    if (recordToSave.isSaving) return;

    setData(old => old.map(r =>
      r === row.original ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      await saveRecord(recordToSave);
      setData(old => old.map(r =>
        r === row.original
          ? { ...r, [column.id]: value, modified: false, isSaving: false, new_record: false }
          : r
      ));
    } catch (err) {
      setData(old => old.map(r => r === row.original ? { ...r, isSaving: false } : r));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save record." });
    }
  };

  const addRecord = () => {
    setData(prev => [{
      value: "",
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  // --- Column Definitions ---
  const createId = useMemo(
    () => createElementIdFactory(positionIdPrefix),
    [positionIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  const columns = useMemo(() => [
    columnHelper.accessor("value", {
      id: "value",
      header: "Position",
      cell: EditableCell,
      size: 250,
    }),
  ], []);

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button
          id={createId("add", "button")}
          variant="secondary"
          size="sm"
          onClick={addRecord}
        >
          Add
        </Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="position-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.position}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default PositionList;